import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { servicesData } from '../mock';
import { Monitor, Smartphone, Settings, HardDrive, Globe, Star, ChevronDown, Phone, Heart, Award } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';

const iconMap = {
  Monitor: <Monitor size={32} />,
  Smartphone: <Smartphone size={32} />,
  Settings: <Settings size={32} />,
  HardDrive: <HardDrive size={32} />,
  Globe: <Globe size={32} />
};

export const ITSupport = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const data = servicesData.itSupport;

  const targetAudience = [
    { group: 'Seniors', description: 'Initiation en douceur aux nouvelles technologies' },
    { group: 'Débutants', description: 'Apprentissage des bases de l\'informatique' },
    { group: 'Tous publics', description: 'Résolution de problèmes techniques' }
  ];

  const testimonials = [
    {
      name: 'Monsieur Durand',
      age: '72 ans',
      rating: 5,
      comment: 'Grâce à leur patience, je peux maintenant utiliser ma tablette pour communiquer avec mes petits-enfants !',
      service: 'Initiation tablette'
    },
    {
      name: 'Anne P.',
      rating: 5,
      comment: 'Explications claires et simples. Mon ordinateur fonctionne parfaitement maintenant.',
      service: 'Dépannage PC'
    }
  ];

  const faqs = [
    {
      question: 'Je ne connais rien à l\'informatique, pouvez-vous m\'aider ?',
      answer: 'Absolument ! Nous sommes spécialisés dans l\'accompagnement des débutants. Nous adaptons notre rythme à votre niveau et vos besoins.'
    },
    {
      question: 'Combien de temps dure une séance ?',
      answer: 'Les séances durent généralement entre 1h et 2h, selon vos besoins et votre rythme d\'apprentissage.'
    },
    {
      question: 'Intervenez-vous sur Mac et PC ?',
      answer: 'Oui, nous intervenons sur tous les systèmes : Windows, Mac, tablettes Android et iPad.'
    },
    {
      question: 'Proposez-vous un suivi régulier ?',
      answer: 'Oui, nous pouvons mettre en place un accompagnement régulier pour vous aider à progresser à votre rythme.'
    }
  ];

  return (
    <div className="page-service">
      <section className="service-hero">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <h1 className="display-medium">{data.title}</h1>
            <p className="body-medium hero-subtitle">{data.description}</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="service-image-section">
        <div className="container">
          <ScrollReveal animation="zoom-in">
            <img 
              src="/images/it-support.jpg" 
              alt="Assistance informatique" 
              className="service-main-image"
            />
          </ScrollReveal>
        </div>
      </section>

      {/* Target Audience */}
      <section className="audience-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Pour Qui ?</h2>
              <p className="body-medium">Une assistance adaptée à tous</p>
            </div>
          </ScrollReveal>
          <div className="audience-grid">
            {targetAudience.map((item, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="audience-card">
                  <h3 className="heading-2">{item.group}</h3>
                  <p className="body-medium">{item.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="service-details">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Nos Services</h2>
            </div>
          </ScrollReveal>
          <div className="network-grid">
            {data.services.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="network-card service-detail-card">
                  <div className="feature-icon">{iconMap[service.icon]}</div>
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="approach-section">
        <div className="container">
          <div className="approach-content">
            <ScrollReveal animation="fade-right">
              <div className="approach-text">
                <h2 className="heading-1">Notre Approche Pédagogique</h2>
                <div className="approach-points">
                  <div className="approach-point">
                    <Heart size={32} />
                    <div>
                      <h3 className="heading-2">Patience et Écoute</h3>
                      <p className="body-medium">Nous prenons le temps nécessaire pour vous expliquer chaque étape</p>
                    </div>
                  </div>
                  <div className="approach-point">
                    <Award size={32} />
                    <div>
                      <h3 className="heading-2">Explications Simples</h3>
                      <p className="body-medium">Pas de jargon technique, des mots clairs et accessibles</p>
                    </div>
                  </div>
              </div>
            </div>
            </ScrollReveal>
            <ScrollReveal animation="fade-left" delay={200}>
              <div className="approach-image">
                <div className="approach-badge">
                  <Monitor size={48} />
                  <p>Formation à votre rythme</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Témoignages</h2>
            </div>
          </ScrollReveal>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="testimonial-card">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                    ))}
                  </div>
                  <p className="testimonial-comment">"{testimonial.comment}"</p>
                  <div className="testimonial-author">
                    <div className="author-info">
                      <strong>{testimonial.name}</strong>
                      <span>{testimonial.service}</span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Questions Fréquentes</h2>
            </div>
          </ScrollReveal>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <div className="faq-item">
                  <button 
                    className={`faq-question ${openFaq === index ? 'active' : ''}`}
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={24} className={`faq-icon ${openFaq === index ? 'rotated' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="faq-answer">
                      <p className="body-medium">{faq.answer}</p>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="cta-content">
              <h2 className="heading-1">Besoin d'aide en informatique ?</h2>
              <p className="body-medium">
                Laissez-nous vous accompagner dans le monde du numérique
              </p>
              <div className="cta-buttons">
                <Link to="/contact#devis" className="btn-primary btn-cta">Demander un devis</Link>
                <a href="tel:0481119591" className="btn-secondary btn-cta">
                  <Phone size={20} />
                  07 69 01 69 04
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};